import React, { useRef, useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import { Button, Tooltip } from '@mui/material'
import Lottie from 'lottie-react'
import fullScreen from '../src/assets/Lottie/fullScreen.json'
import { FULLSCREEN_TOGGGLE_TEXT } from 'src/utils/constants/common'

const FullScreenButton = () => {
  const classes = useStyles()
  const isFullScreenRef = useRef(false)
  const [isFullScreen, setIsFullScreen] = useState(false)

  const toolTipText = isFullScreen
    ? FULLSCREEN_TOGGGLE_TEXT?.EXIT_FULL_SCREEN
    : FULLSCREEN_TOGGGLE_TEXT?.FULL_SCREEN

  const updateFullScreenStatus = () => {
    const isFullScreen = !!(
      (
        document?.fullscreenElement ||
        document?.mozFullScreenElement ||
        document?.webkitFullscreenElement ||
        document?.msFullscreenElement ||
        (window?.innerHeight === window?.screen?.height &&
          window?.innerWidth === window?.screen?.width)
      ) // Detect manual fullscreen like Fn + F11
    )
    isFullScreenRef.current = isFullScreen
    setIsFullScreen(isFullScreen)
  }

  useEffect(() => {
    updateFullScreenStatus()

    // Listen for fullscreen change events
    document?.addEventListener('fullscreenchange', updateFullScreenStatus)
    document?.addEventListener('mozfullscreenchange', updateFullScreenStatus)
    document?.addEventListener('webkitfullscreenchange', updateFullScreenStatus)
    document?.addEventListener('msfullscreenchange', updateFullScreenStatus)
    window?.addEventListener('resize', updateFullScreenStatus)

    return () => {
      document?.removeEventListener('fullscreenchange', updateFullScreenStatus)
      document?.removeEventListener(
        'mozfullscreenchange',
        updateFullScreenStatus
      )
      document?.removeEventListener(
        'webkitfullscreenchange',
        updateFullScreenStatus
      )
      document?.removeEventListener(
        'msfullscreenchange',
        updateFullScreenStatus
      )
      window?.removeEventListener('resize', updateFullScreenStatus) // Remove resize event
    }
  }, [])

  const toggleFullScreen = () => {
    if (!isFullScreenRef?.current) {
      if (document?.documentElement?.requestFullscreen) {
        document?.documentElement?.requestFullscreen()
      } else if (document?.documentElement?.mozRequestFullScreen) {
        document?.documentElement?.mozRequestFullScreen()
      } else if (document?.documentElement?.webkitRequestFullscreen) {
        document?.documentElement?.webkitRequestFullscreen()
      } else if (document?.documentElement?.msRequestFullscreen) {
        document?.documentElement?.msRequestFullscreen()
      }
    } else {
      if (document?.exitFullscreen) {
        document?.exitFullscreen()
      } else if (document?.mozCancelFullScreen) {
        document?.mozCancelFullScreen()
      } else if (document?.webkitExitFullscreen) {
        document?.webkitExitFullscreen()
      } else if (document?.msExitFullscreen) {
        document?.msExitFullscreen()
      }
    }
  }

  return (
    <Tooltip data-testid='tooltip' title={toolTipText}>
      <Button
        onClick={(e) => {
          e?.preventDefault()
          toggleFullScreen()
        }}
        className={`${classes.fullScreenToggleButton} ${
          !isFullScreen ? classes.zooming : ''
        }`}
      >
        {isFullScreen ? (
          <FullscreenExitIcon fontSize='medium' />
        ) : (
          <Lottie
            animationData={fullScreen}
            loop={true}
            className={classes.lottieAnimation}
          />
        )}
      </Button>
    </Tooltip>
  )
}

export default FullScreenButton

const useStyles = makeStyles({
  fullScreenToggleButton: {
    background: 'none',
    zIndex: 1100,
    transition: 'transform 0.1s',
    '&:hover': {
      transform: 'scale(1.5)',
      background: 'none',
    },
  },
  zooming: {
    animation: '$zoomAnimation 0.5s infinite',
  },
  '@keyframes zoomAnimation': {
    '0%': {
      transform: 'scale(1)',
    },
    '50%': {
      transform: 'scale(1.2)',
    },
    '100%': {
      transform: 'scale(1)',
    },
  },
  lottieAnimation: {
    width: '70%',
    height: '80%',
  },
})
